import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { IssueRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type { IssueRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIssuesQuery,
  Issue_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIssuesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

function mapTrpcIssueToGraphQL(
  issue: IssueRegisterResponseRow
): StrictMappedGraphQLSelection<GetIssuesQuery['issue'][number]> {
  return {
    ...issue,
    // `getIssuesRegisterQueryConfig` pre-filters `actions` to Status = open, so
    // `.length` is the open-action count the GraphQL aggregate exposed. Do not
    // re-filter here — and do not drop the predicate server-side, or this
    // silently becomes a total (COI-747).
    actions_aggregate: {
      aggregate: {
        count: issue.actions.length,
      },
    },
  };
}

/**
 * Maps TRPC issue data to match the GraphQL query structure
 * Exported for use in data sources
 */
export function mapTrpcIssuesToGraphQL(
  trpcData: IssueRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetIssuesQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    issue: trpcData.issue.map((issue) => mapTrpcIssueToGraphQL(issue)),
  };
}

type InternalArgs = {
  issueType: ParentIssueType;
  entityIds?: string[];
};

const useIssueRegisterGraphqlVariables = ({ issueType }: InternalArgs) => {
  const where = useEntityWhereFilter<Issue_Bool_Exp>(Parent_Type_Enum.Issue, {
    Type: {
      _eq: issueType,
    },
  });

  return { where };
};

const useGetIssueRegisterBase = createQueryHook<
  InternalArgs,
  IssueRegisterResponse,
  GetIssuesQuery
>({
  trpcQueryOptions: (trpc, { issueType, entityIds }) =>
    trpc.frontend.issue.register.queryOptions({ issueType, entityIds }),
  mapTrpcDataToGraphQL: (data) => ({
    issue: data.issue.map((issue) => mapTrpcIssueToGraphQL(issue)),
  }),
  graphqlDocument: GetIssuesDocument,
  graphqlVariables: useIssueRegisterGraphqlVariables,
});

export type UseGetIssueRegisterArgs = {
  issueType: ParentIssueType;
};

/**
 * Fetches issues with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses useEntityFilter context internally — consumers
 * don't need to pass entityIds.
 */
export const useGetIssueRegister = (params: {
  queryArgs: UseGetIssueRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetIssueRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
